import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useUploadPhoto, type UploadPurpose } from '@/hooks/useUploadPhoto';
import styles from './PhotoUploader.module.css';

interface PhotoUploaderProps {
  purpose: UploadPurpose;
  // Existing remote photo URL (rendered as preview if no local file picked).
  existingUrl?: string | null;
  // Called once the file has been uploaded and the backend assigned an
  // object_key. Parent is expected to persist that key on its own DTO.
  onUploaded: (objectKey: string) => void;
  // Optional caption shown above the preview area (i18n key recommended).
  label?: string;
  // Round avatar variant — used in profile screens.
  shape?: 'square' | 'circle';
}

// PhotoUploader — file picker + preview + presign+PUT flow.
//
// Compression happens inside useUploadPhoto (≤1 MB JPEG/PNG/WebP). Error
// surface is intentionally simple: a small caption under the button. The
// parent screen can additionally toast on its own success path.
export function PhotoUploader({
  purpose,
  existingUrl,
  onUploaded,
  label,
  shape = 'square',
}: PhotoUploaderProps): JSX.Element {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, uploading, error, reset } = useUploadPhoto(purpose);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [uploadedKey, setUploadedKey] = useState<string | null>(null);

  // Free object-URL previews when component unmounts or preview replaced.
  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const handlePick = () => inputRef.current?.click();

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    reset();
    // Show preview immediately — compression runs in a worker so the
    // user sees something instantly even on a slow phone.
    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview(URL.createObjectURL(file));
    try {
      const r = await upload(file);
      setUploadedKey(r.objectKey);
      onUploaded(r.objectKey);
    } catch {
      // error is exposed below via the `error` state from the hook.
    }
  };

  const previewUrl = localPreview ?? existingUrl ?? null;
  const errorMsgKey =
    error === 'errors.upload.bad_type' ? 'errors.upload.bad_type' : 'errors.upload.failed';

  return (
    <div className={styles.wrap}>
      {label && <div className={styles.label}>{label}</div>}
      <div
        className={[styles.preview, shape === 'circle' ? styles.circle : ''].filter(Boolean).join(' ')}
        role="img"
        aria-label={t('uploads.preview_aria')}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="" />
        ) : (
          <span className={styles.placeholder}>{t('uploads.placeholder')}</span>
        )}
        {uploading && (
          <div className={styles.overlay}>
            <Spinner />
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleChange}
        hidden
        // do not auto-open camera on desktop; mobile browsers still treat
        // this as the system picker (which already offers camera).
      />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        block
        onClick={handlePick}
        loading={uploading}
        disabled={uploading}
      >
        {existingUrl || uploadedKey || localPreview
          ? t('uploads.replace')
          : t('uploads.pick')}
      </Button>
      {error && <div className={styles.error}>{t(errorMsgKey)}</div>}
    </div>
  );
}
